/**
 * AI照片王 进度事件业务化文案映射 —— **本地 fallback 表**。
 *
 * 主路径:后端 SSE 的 tool_start.data.label 已经带业务化文案 {running, done, detail?},
 * 由 xiaohei-claw 各工具的 LabelProvider 自己产出(内聚在工具实现里,见
 * xiaohei-claw/internal/tools/display_labels.go 与 internal/tools/mcp/display_labels.go)。
 * 前端 AiPhotoJunPanel.tsx 在 SSE 解析时优先用后端 label。
 *
 * 本表只在以下情况兜底:
 *   - 老版本后端(未推 label 字段)
 *   - 后端 label 字段缺失 / 格式异常
 *
 * 兜底原则:
 *   - 用动词起头("在翻你的数据…"),让用户感知 AI 正在"动手帮我"
 *   - done 不要"思考完毕/思考中"这种空泛词,换具体动作完成态
 *   - 永远不把内部工具名 / DSL 原样暴露给用户(参考 resolveProgressDetail 的 mcp_tool_search
 *     分支被砍掉的原因:"select:get_group_photo_sta..." 这类 query 不能透回去)
 */

export interface ProgressLabel {
  /** 运行中展示,比如 "在翻你的数据…" */
  running: string
  /** 运行结束展示,比如 "数据已拿到" */
  done: string
}

export interface ProgressStep {
  id: string
  label: ProgressLabel
  status: 'running' | 'ok' | 'fail'
  /** 可选的一行补充说明(已做脱敏),折叠态不显示 */
  detail?: string
  /** 毫秒时间戳,用于同一 round 内相同 tool 的 start/end 配对 */
  ts: number
  // ---- 以下字段仅供「调用链路」诊断弹窗使用,普通用户看不到 ----
  /** 原始工具名(mcp_call / get_photos_count / render_card / ...) */
  tool_name?: string
  /** tool_start 时的原始 args JSON 字符串 */
  args_raw?: string
  /** tool_end 时的 result_preview 字符串(可能截断) */
  result_preview?: string
  /** tool_start 毫秒时间戳 */
  started_at?: number
  /** tool_end 毫秒时间戳 */
  ended_at?: number
  /** SSE round 序号(0-based) */
  round?: number
}

/** 顶层工具名 → 业务文案。蓝领 / 业务能秒懂,带动作感。done 不出现"思考"词。 */
const TOP_TOOL_LABELS: Record<string, ProgressLabel> = {
  mcp_tool_search: {
    running: '正在挑合适的工具…',
    done: '工具已挑好'
  },
  mcp_call: {
    running: '正在帮你做这件事…',
    done: '已完成'
  },
  shell_execute: {
    running: '正在帮你算结果…',
    done: '算完了'
  },
  web_search: {
    running: '正在网上帮你查…',
    done: '资料已找到'
  },
  generate_image: {
    running: '正在帮你画图…',
    done: '图画好了'
  },
  analyze_image: {
    running: '正在看这张照片…',
    done: '照片看完了'
  },
  publish_artifact: {
    running: '正在打包文件…',
    done: '文件已就绪'
  },
  sub_agent: {
    running: '派个小助手去做…',
    done: '小助手完成了'
  },
  skill: {
    running: '正在调用一个专用工具…',
    done: '工具已用上'
  },
  render_card: {
    running: '正在排版给你看…',
    done: '表格加载完成'
  },
  ask_user_question: {
    running: '等你回答一下…',
    done: '收到你的回答'
  }
}

/**
 * MCP 工具名 → 业务动作(args.name 匹配)。
 * 按日志频次逐步补齐;未命中就用 mcp_call 的通用文案。
 * key 用精确匹配的全名,不做正则,避免误伤。
 */
const MCP_NAME_LABELS: Record<string, ProgressLabel> = {
  // 团队额度 / 成员容量
  xhey_tools_get_group_storage_quota: {
    running: '在看你团队还剩多少存储空间…',
    done: '团队存储情况已看清'
  },
  xhey_tools_set_group_storage_quota: {
    running: '正在调你团队的存储上限…',
    done: '团队存储上限已调好'
  },
  xhey_tools_get_group_size_quota: {
    running: '在看你团队还能加几个人…',
    done: '团队人数上限已看清'
  },
  xhey_tools_set_group_size_quota: {
    running: '正在调你团队的人数上限…',
    done: '团队人数上限已调好'
  },
  // 风险 / 作弊
  xhey_tools_get_cheat_app_by_device: {
    running: '正在查这台手机有没问题…',
    done: '设备情况已查清'
  },
  xhey_tools_get_cheat_app_by_phone: {
    running: '正在查这个账号有没问题…',
    done: '账号情况已查清'
  },
  xhey_tools_black_device_verify: {
    running: '正在核验这台设备…',
    done: '设备已核验完'
  },
  xhey_tools_anti_code_verify: {
    running: '正在核对防伪码…',
    done: '防伪码核对完毕'
  },
  // 会员
  xhey_tools_clear_group_vip_test: {
    running: '正在清这个团队的测试会员痕迹…',
    done: '团队测试痕迹已清完'
  },
  xhey_tools_clear_global_vip_test: {
    running: '正在清全局的测试会员痕迹…',
    done: '全局测试痕迹已清完'
  },
  xhey_tools_set_group_vip_expire_test: {
    running: '正在改这个团队的会员到期日…',
    done: '团队会员到期日已改好'
  },
  order_vip_team_send: {
    running: '正在给团队发会员…',
    done: '团队会员已送达'
  },
  order_vip_fault_send: {
    running: '正在给用户补发会员…',
    done: '会员已补到位'
  },
  xhey_tools_mark_invoice_issued: {
    running: '正在更新发票状态…',
    done: '发票状态已更新'
  }
}

const FALLBACK_LABEL: ProgressLabel = {
  running: '正在思考…',
  done: '已完成'
}

/**
 * 根据 tool_start / tool_end 事件体推导业务文案。
 * 入参 argsRaw 是后端原样透传的 JSON 字符串(可能被截断),这里只做"尽量解析",失败就不用。
 */
export function resolveProgressLabel(
  toolName: string,
  argsRaw?: string
): ProgressLabel {
  if (toolName === 'mcp_call' && argsRaw) {
    const mcpName = safeExtract(argsRaw, 'name')
    if (mcpName && MCP_NAME_LABELS[mcpName]) {
      return MCP_NAME_LABELS[mcpName]
    }
    // 未命中精确表,但能拿到 name 时,做一次"温和"派生:把下划线去掉、首字母不变
    if (mcpName) {
      return TOP_TOOL_LABELS.mcp_call
    }
  }
  if (TOP_TOOL_LABELS[toolName]) {
    return TOP_TOOL_LABELS[toolName]
  }
  return FALLBACK_LABEL
}

/**
 * 从 argsRaw 里把一个**展示用**的一句话 detail 抽出来。
 * 只在折叠态展开时才会显示,不重要但能让用户知道到底在干嘛。
 * 绝对**不**把原始 JSON 或 ID 露出来。
 */
export function resolveProgressDetail(
  toolName: string,
  argsRaw?: string
): string | undefined {
  if (!argsRaw) return undefined
  if (toolName === 'web_search') {
    const q = safeExtract(argsRaw, 'query')
    return q ? `查的是:${trimForUI(q, 40)}` : undefined
  }
  if (toolName === 'generate_image') {
    const p = safeExtract(argsRaw, 'prompt')
    return p ? `画的是:${trimForUI(p, 40)}` : undefined
  }
  if (toolName === 'mcp_tool_search') {
    // 不透 query —— 它的格式是内部 DSL("select:xxx" / "names:xxx" / 关键词检索),
    // 直接 echo 给用户就是技术黑话(刘乾隆图2 反馈过)。本地 fallback 不出 detail。
    return undefined
  }
  if (toolName === 'skill') {
    const name = safeExtract(argsRaw, 'name')
    return name ? `用的工具:${trimForUI(name, 30)}` : undefined
  }
  if (toolName === 'sub_agent') {
    const task = safeExtract(argsRaw, 'task')
    return task ? `交办的事:${trimForUI(task, 40)}` : undefined
  }
  if (toolName === 'render_card') {
    const type = safeExtract(argsRaw, 'type')
    if (type === 'ranking') return '排成榜单'
    if (type === 'stat') return '汇成一个数'
    if (type === 'html') return '画成图表'
    return undefined
  }
  // mcp_call 除非在 label 已命中的精细名,否则不给 detail(避免露出 xhey_tools_xxx 的原名)
  return undefined
}

/**
 * safeExtract 用于从 args JSON 里抽一个顶层字符串字段。
 * args 可能是截断过的(后端会在 1500 字符处 …(已截断)),先宽松 JSON.parse,失败再用正则兜底。
 */
function safeExtract(raw: string, key: string): string {
  const s = raw.trim()
  if (!s) return ''
  try {
    const obj = JSON.parse(s)
    if (obj && typeof obj === 'object' && typeof obj[key] === 'string') {
      return obj[key]
    }
  } catch {
    // 降级:正则匹配 "key":"value"(值里不能直接有未转义的引号,够用了)
    const re = new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`)
    const m = s.match(re)
    if (m && m[1]) {
      try {
        return JSON.parse(`"${m[1]}"`)
      } catch {
        return m[1]
      }
    }
  }
  return ''
}

function trimForUI(s: string, max: number) {
  if (!s) return ''
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}
